import { faXmark } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { Fragment, useCallback, useState } from 'react'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import TwinToolTip from '../../../baseComponents/TwinToolTip'
import { DropDownWithSearchBarAndCheckbox } from '.././DropDownCheckbox'
import { flushSync } from 'react-dom'
import CustomSelect, { CustomSelectHeaderDefaultProps, CustomSelectProps } from '..'
import { CustomSelectDropdownIcon } from '../DropDown'

export interface CustomSelectSearchBarWithCheckBoxStateLessProps extends CustomSelectProps {
    items: TwinDictionary
    selectedValues: string[] | number[]
    className?: string
    children?: React.ReactNode
    displayTagsLength?: number
    handleOnRemove: (value: number | string) => void
}

export const CustomSelectSearchBarWithCheckBoxStateLess: React.FC<CustomSelectSearchBarWithCheckBoxStateLessProps> = ({ items, className, children, selectedValues, ...all }) => {
    return (
        <div className={'flex flex-col gap-y-15 c_s_tags_box_parent ' + (className || '')}>
            <CustomSelect items={items} {...all} className='c_s_searchbar_checkbox_tags_parent max-w-600' DropDownComponent={DropDownWithSearchBarAndCheckbox} />
            {children}
            <CustomSelectTagList items={items} selectedValues={selectedValues} {...all} />
            {all?.name && <input type='hidden' name={all?.name} value={selectedValues.join(';')} readOnly twin-type='custom-select-checkbox-tag' />}
        </div>
    )
}

export interface CustomSelectSearchBarWithCheckBoxBaseStateFullProps extends CustomSelectProps {
    items: TwinDictionary
    className?: string
    classNameTags?: string
    children?: React.ReactNode
    displayTagsLength?: number
    selectedValues: string[] | number[]
}

export const CustomSelectSearchBarWithCheckBoxStateFull: React.FC<CustomSelectSearchBarWithCheckBoxBaseStateFullProps> = ({ items, className, children, selectedValues, onChange, classNameTags, ...all }) => {
    const { markAllAsSelected, handleOnRemove, myOnChange, mySelectedValues } = useCustomSelectSearchBarWithCheckBoxStateFullLogic({ onChange, selectedValues })
    return (
        <div className={'flex flex-col gap-y-15 c_s_tags_box_parent c_s_tags_box_white_input relative ' + (className || '')}>
            <CustomSelect items={items} {...all} className='c_s_searchbar_checkbox_tags_parent max-w-600' value={mySelectedValues?.[0]} extraDropDownParams={{ markAllAsSelected, selectedValues: mySelectedValues, onChange: myOnChange, ...all.extraDropDownParams }}
                DropDownComponent={DropDownWithSearchBarAndCheckbox} />
            {children}
            <CustomSelectTagList items={items} selectedValues={mySelectedValues} handleOnRemove={handleOnRemove} {...all} />
            {all?.name && <input type='hidden' name={all?.name} value={mySelectedValues.join(';')} readOnly twin-type='custom-select-checkbox-tag' />}
        </div>
    )
}

export const CustomSSearchBarWithHeaderInnerTags: React.FC<CustomSelectSearchBarWithCheckBoxBaseStateFullProps> = ({ items, className, children, selectedValues, onChange, classNameTags, ...all }) => {
    const { markAllAsSelected, handleOnRemove, myOnChange, mySelectedValues } = useCustomSelectSearchBarWithCheckBoxStateFullLogic({ onChange, selectedValues })
    return (
        <div className={'flex flex-col gap-y-15 c_s_tags_box_parent c_s_tags_box_white_input relative ' + (className || '')}>
            <CustomSelect items={items} {...all} extraHeaderParams={{ selectedValues: mySelectedValues, handleOnRemove }} className='c_s_searchbar_checkbox_tags_parent max-w-600' value={mySelectedValues?.[0]} extraDropDownParams={{ markAllAsSelected, selectedValues: mySelectedValues, onChange: myOnChange, ...all.extraDropDownParams }} DropDownComponent={DropDownWithSearchBarAndCheckbox} CustomSelectHeader={CustomeHeaderTags} />
            {children}
            {all?.name && <input type='hidden' name={all?.name} value={mySelectedValues.join(';')} readOnly twin-type='custom-select-checkbox-tag' />}
        </div>
    )
}

interface CustomeHeaderTagsProps extends CustomSelectHeaderDefaultProps {}

const CustomeHeaderTags: React.FC<CustomeHeaderTagsProps> = ({ opened, displayText, label, required, icon, extraHeaderParams, items, placeholder, onClickIcon, ...all }) => {
    const { myOnRemove } = useCustomeHeaderTagsLogic({ handleOnRemove: extraHeaderParams?.handleOnRemove })
    return (
        <div className={'custom_select_header custom_select_header_white_input items-center cursor-pointer '}>
            <input type='text' className='input_hidden' {...all} required={required} onChange={() => undefined} />
            <div className='custom_select_header_icon'>
                {extraHeaderParams?.selectedValues && extraHeaderParams?.selectedValues.length ?
                    <CustomSelectTagList items={items} selectedValues={extraHeaderParams?.selectedValues} handleOnRemove={myOnRemove} displayTagsLength={2} {...all} /> 
                    : <div className='custom_select_header_text'><label title={placeholder ?? label} className={'cursor-pointer label_placeholder twin_elipsis'}>{placeholder ?? label}</label></div>
                }
                <CustomSelectDropdownIcon opened={opened} readOnly={all.readOnly} icon={icon} onClickIcon={onClickIcon} />
            </div>
        </div>
    )
}

interface CustomeHeaderTagsLogicProps {
    handleOnRemove: (value: string | number) => Promise<void>
}

const useCustomeHeaderTagsLogic = ({ handleOnRemove }: CustomeHeaderTagsLogicProps) => {
    const myOnRemove = useCallback((value: any, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation()
        handleOnRemove(value)
    }, [handleOnRemove])

    return { myOnRemove }
}

interface CustomSelectSearchBarWithCheckBoxStateFullLogicProps {
    onChange: ((values: any) => void) | undefined
    selectedValues: string[] | number[]
}

const useCustomSelectSearchBarWithCheckBoxStateFullLogic = ({ onChange, selectedValues }: CustomSelectSearchBarWithCheckBoxStateFullLogicProps) => {
    const [mySelectedValues, setMySelectedValues] = useState(selectedValues || [])

    const myOnChange = useCallback(async (value: string) => {
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = [...copyOld]
                const position = currentItems.indexOf(value)
                if (position !== -1) {
                    currentItems.splice(position, 1)
                } else {
                    currentItems.push(String(value))
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange?.(myCurrentItems)
        })
    }, [onChange])

    const markAllAsSelected = useCallback(async (e: any, values: TwinDictionary[], isAllSelected: boolean) => {
        e.stopPropagation()
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = copyOld
                for (const item of values) {
                    const value = String(item.id)
                    if (isAllSelected) {
                        const index = currentItems.indexOf(value)
                        if (index !== -1) {
                            currentItems.splice(index, 1)
                        }
                    } else {
                        if (!currentItems.includes(value)) {
                            currentItems.push(value)
                        }
                    }
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange?.(myCurrentItems)
        })
    }, [onChange])

    const handleOnRemove = useCallback(async (value: string | number) => {
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = copyOld
                const position = currentItems.indexOf(value)
                if (position !== -1) {
                    currentItems.splice(position, 1)
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange?.(myCurrentItems)
        })
    }, [onChange])

    return { myOnChange, handleOnRemove, markAllAsSelected, mySelectedValues }
}

export interface CustomSelectSearchBarWithCheckBoxColorStateLessProps extends CustomSelectProps {
    items: TwinDictionary
    selectedValues: string[] | number[]
    className?: string
    children?: React.ReactNode
    handleOnRemove: (value: number | string) => void
    displayTagsLength?: number
}

export const CustomSelectSearchBarWithCheckBoxColorStateLess: React.FC<CustomSelectSearchBarWithCheckBoxColorStateLessProps> = ({ items, className, children, selectedValues, ...all }) => {
    return (
        <div className={'flex flex-col gap-y-15 relative c_s_tags_color_box_parent ' + (className || '')}>
            <CustomSelect items={items} {...all} className='c_s_searchbar_checkbox_tags_parent max-w-600'
                DropDownComponent={DropDownWithSearchBarAndCheckbox} />
            {children}
            {selectedValues?.length ? <CustomSelectTagListWithStatusColor items={items} selectedValues={selectedValues} {...all} /> : null}
            {all?.name && <input type='hidden' name={all?.name} value={selectedValues.join(';')} readOnly twin-type='custom-select-checkbox-tag' />}
        </div>
    )
}

export const CustomSelectSearchBarWithCheckBoxColorStateFull: React.FC<CustomSelectSearchBarWithCheckBoxBaseStateFullProps> = ({ items, className, children, selectedValues, onChange, ...all }) => {
    const { markAllAsSelected, handleOnRemove, myOnChange, mySelectedValues } = useCustomSelectSearchBarWithCheckBoxStateFullLogic({ onChange, selectedValues })
    return (
        <div className={'flex flex-col gap-y-15 relative c_s_tags_color_box_parent ' + (className || '')}>
            <CustomSelect items={items} {...all} className='c_s_searchbar_checkbox_tags_parent max-w-600' extraDropDownParams={{ markAllAsSelected, selectedValues: mySelectedValues, onChange: myOnChange, ...all.extraDropDownParams }}
                DropDownComponent={DropDownWithSearchBarAndCheckbox} />
            {children}
            <CustomSelectTagListWithStatusColor items={items} selectedValues={mySelectedValues} handleOnRemove={handleOnRemove} {...all} />
            {all?.name && <input type='hidden' name={all?.name} value={mySelectedValues.join(';')} readOnly twin-type='custom-select-checkbox-tag' />}
        </div>
    )
}

interface CustomSelectTagListProps {
    selectedValues: any[]
    items: TwinDictionary
    readOnly?: boolean
    displayTagsLength?: number
    handleOnRemove: (value: string | number, e: React.MouseEvent) => void
}

const CustomSelectTagList: React.FC<CustomSelectTagListProps> = ({ items, selectedValues, readOnly, handleOnRemove, displayTagsLength = 4 }) => {
    const renderTags: JSX.Element[] = []
    const renderHiddenTags: JSX.Element[] = []
    for (const key in selectedValues) {
        const selectedValue = selectedValues[key]
        const item = items[selectedValue]
        if (selectedValues.length > displayTagsLength) {
            if (parseInt(key) < (displayTagsLength - 1)) {
                renderTags.push(
                    <CustomSelectTag readOnly={readOnly} text={item?.name} key={item?.name + '_tag'} id={item?.id} onClick={(e) => handleOnRemove(selectedValue, e)} />
                )
            } else {
                renderHiddenTags.push(
                    <HiddenTag readOnly={readOnly} key={'customSelectTag' + selectedValue} onClick={(e) => handleOnRemove(selectedValue, e)} text={item?.name} />
                )
            }
        } else {
            renderTags.push(
                <CustomSelectTag readOnly={readOnly} text={item?.name} key={item?.name + '_tag'} id={item?.id} onClick={(e) => handleOnRemove(selectedValue, e)} />
            )
        }
    }

    return (
        <CustomSelectTagListBase tags={renderTags} hiddenTags={renderHiddenTags} />
    )
}

interface CustomSelectTagProps {
    text: string
    onClick: (e: React.MouseEvent) => void
    id: number | string
    readOnly?: boolean
}

const CustomSelectTag: React.FC<CustomSelectTagProps> = ({ text, onClick, id, readOnly }) => {
    return (
        <div className='flex items-center gap-x-8 bg-green-DB px-10 py-5 w-fit rounded-full custom_select_tag' id={'singleCustomSelectTag' + id}>
            <div className='flex-auto inline-block twin_elipsis'>{text}</div>
            {!readOnly ? <TwinIcon icon={faXmark} className='cursor-pointer h-16 w-16' onClick={onClick} /> : null}
            <TwinToolTip place='top' anchorSelect={'#singleCustomSelectTag' + id} className='max-w-300'>
                <div>{text}</div>
            </TwinToolTip>
        </div>
    )
}

interface HiddenTagProps {
    onClick: (e: React.MouseEvent) => void
    text: string
    key: string
    readOnly?: boolean
}

const HiddenTag: React.FC<HiddenTagProps> = ({ key, onClick, text, readOnly }) => {
    return (
        <div key={key}>
            {text}
            {!readOnly ? <TwinIcon icon={faXmark} className='cursor-pointer ml-10 h-14 w-14' onClick={onClick} /> : null}
        </div>
    )
}

interface CustomSelectTagListBaseProps {
    tags: JSX.Element[]
    hiddenTags: JSX.Element[]
    text?: string
    className?: string
}

const CustomSelectTagListBase: React.FC<CustomSelectTagListBaseProps> = ({ hiddenTags, tags, text, className }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='flex flex-row gap-x-10 gap-y-10 flex-wrap custom_select_tag_list_box_parent'>
            {tags}
            {hiddenTags?.length ?
                <Fragment>
                    <div className={'flex items-center gap-x-10 bg-green-DB px-15 py-5 w-fit custom_select_tag rounded-full ' + (className || '')} id='customSelectTag'>{text ?? t('seeMoreDots', 'Ver más...')}</div>
                    <DisplayCustomSelectHiddenTags renderTags={hiddenTags} />
                </Fragment>
                : null
            }
        </div>
    )
}

interface DisplayCustomSelectHiddenTagsProps {
    renderTags: JSX.Element[]
}

const DisplayCustomSelectHiddenTags: React.FC<DisplayCustomSelectHiddenTagsProps> = ({ renderTags }) => {
    return (
        <TwinToolTip place='top' anchorSelect='#customSelectTag' clickable className='max-w-300'>
            <div>
                {renderTags}
            </div>
        </TwinToolTip>
    )
}

export const CustomSelectTagListWithStatusColor: React.FC<CustomSelectTagListProps> = ({ items, selectedValues, readOnly, handleOnRemove, displayTagsLength = 4 }) => {
    const renderTags: JSX.Element[] = []
    const renderHiddenTags: JSX.Element[] = []

    for (const key in selectedValues) {
        const selectedValue = selectedValues[key]
        const item = items[selectedValue]
        if (selectedValues.length > displayTagsLength) {
            if (parseInt(key) < (displayTagsLength - 1)) {
                renderTags.push(
                    <CustomSelectTagWithStatusColor readOnly={readOnly} id={item?.id} color={item?.color} text={item?.name} key={item?.name + '_tag'} onClick={(e) => handleOnRemove(selectedValue, e)} />
                )
            } else {
                renderHiddenTags.push(
                    <HiddenTag readOnly={readOnly} key={'customSelectTag' + selectedValue} onClick={(e) => handleOnRemove(selectedValue, e)} text={item?.name} />
                )
            }
        } else {
            renderTags.push(
                <CustomSelectTagWithStatusColor readOnly={readOnly} id={item?.id} color={item?.color} text={item?.name} key={item?.name + '_tag'} onClick={(e) => handleOnRemove(selectedValue, e)} />
            )
        }
    }

    return (
        <CustomSelectTagListBase tags={renderTags} hiddenTags={renderHiddenTags} className='custom_select_tag custom_select_tag_color' text='...' />
    )
}

interface CustomSelectTagWithStatusColorProps {
    text: string
    onClick: (e: React.MouseEvent) => void
    color: string
    id: string | number
    readOnly?: boolean
}

const CustomSelectTagWithStatusColor: React.FC<CustomSelectTagWithStatusColorProps> = ({ text, onClick, color, id, readOnly }) => {
    return (
        <div className='flex items-center gap-x-8 px-10 py-5 w-fit rounded-full custom_select_tag custom_select_tag_color' id={'singleCustomSelectTagColor' + id}>
            <div className='rounded-full min-w-12 h-12' style={{
                background: color || '#979797'
            }} />
            <div className='flex-auto inline-block twin_elipsis'>{text}</div>
            {!readOnly ? <TwinIcon icon={faXmark} className='cursor-pointer h-16 w-16' onClick={onClick} /> : null}
            <TwinToolTip place='top' anchorSelect={'#singleCustomSelectTagColor' + id} className='max-w-300'>
                <div>{text}</div>
            </TwinToolTip>
        </div>
    )
}

export default CustomSelectTagList